import { modelOptions, mongoose, post, prop } from "@typegoose/typegoose";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";

const SCHEMA_KEY = "TtiApplication" as const;

const DEFAULT_BASE_URL_FORMAT = (region: "eu1" | "nam1") =>
    `https://verdi-expeditions.${region}.cloud.thethings.industries/api/v3`;

// End-Device Registry and Identity Server APIs are only available in the eu1 cluster.
// https://www.thethingsindustries.com/docs/concepts/ttn/addresses/#api-endpoints
// eslint-disable-next-line new-cap
export const DEFAULT_END_DEVICE_REGISTRY_BASE_URL = DEFAULT_BASE_URL_FORMAT("eu1");

// Application Server, Join Server, and Network Server APIs are available in all regional clusters,
// so downlink webhook traffic can safely default through nam1.
// eslint-disable-next-line new-cap
const DEFAULT_NETWORK_SERVER_API_BASE_URL = DEFAULT_BASE_URL_FORMAT("nam1");

// Gateway Server APIs are regional and Verdi gateways are registered on nam1.
// eslint-disable-next-line new-cap
export const GATEWAY_SERVER_BASE_URL = DEFAULT_BASE_URL_FORMAT("nam1");

const DEFAULT_DOWNLINK_WEBHOOK_URL_FORMAT =
    `${DEFAULT_NETWORK_SERVER_API_BASE_URL}/as/applications/\${application_id}/webhooks/sending-downlinks/devices/\${device_id}/down/replace` as const;

@modelOptions({
    schemaOptions: {
        collection: "ttiapplications",
        timestamps: true,
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
    },
})
@post<SchemaBase<TtiApplication>>(...addSchemaKeyHook(SCHEMA_KEY))
export class TtiApplication extends SchemaBase<TtiApplication, "webhookFormatString"> {
    static schemaKey = SCHEMA_KEY;
    readonly schemaKey = SCHEMA_KEY;

    public _id!: mongoose.Types.ObjectId;

    @prop({ type: () => String, required: true, unique: true })
    public appID!: string;

    @prop({ type: () => String, required: true, unique: true })
    public apiKey!: string;

    @prop({
        type: () => String,
        required: true,
        default: DEFAULT_DOWNLINK_WEBHOOK_URL_FORMAT,
    })
    public webhookFormatString: string = DEFAULT_DOWNLINK_WEBHOOK_URL_FORMAT;

    @prop({ type: () => String, required: false, default: DEFAULT_END_DEVICE_REGISTRY_BASE_URL })
    public endDeviceRegistryBaseUrl: string = DEFAULT_END_DEVICE_REGISTRY_BASE_URL;

    @prop({ type: () => String, required: false })
    public uptimeRobotHeartbeatMonitorURL?: string;

    static getters = {} as const satisfies Record<string, (self: TtiApplication) => any>;

    static utilities = {
        /**
         * Formats the webhook URL for a specific node ID
         */
        formatWebhookUrl: (self: TtiApplication, nodeID: string): string =>
            self.webhookFormatString.replace("${application_id}", self.appID).replace("${device_id}", nodeID),
    } as const satisfies Record<string, (...args: any) => any>;

    constructor(properties?: SchemaConstructorArgs<TtiApplication, "webhookFormatString">) {
        super(properties);
        Object.assign(this, properties);
    }
}
